import { useEffect, useState } from 'react'
import { ReadyState } from 'react-use-websocket'
import { SendJsonMessage } from 'react-use-websocket/dist/lib/types'
import { Content as ContentType } from '@phrasee/phrasee-typings'
import { useQueryClient } from '@tanstack/react-query'

import { errorToast } from 'common/components/toastNotification'
import { useDocumentTitle } from 'common/hooks/custom'
import { useAppDispatch } from 'common/hooks/redux'
import useWebsockets, { WsResponseBase } from 'common/hooks/useWebsockets'

import LoadingOverlay from '../../../common/components/LoadingOverlay'
import { contentKeys } from '../api/queryKeys'
import MainContent from '../components/MainContent'
import {
  contentPageCleanup,
  toggleElementsDisabled,
  workflowCardClicked,
} from '../store/contentSlice'

import Header from './content/components/Header'
import Content from './content'
import { getAllComponentElements } from './helpers'
import { useContent } from './hooks'
import Preview from './preview'
import Workflow from './workflow'

const phrases = [
  'Preparing your content',
  'Generating ideas',
  'Crafting the perfect content',
  'Searching for that extra bit of awesomeness...',
  'Refining your masterpiece',
  'Finalizing the details',
  "Don't worry, we're almost ready to roll!",
  'Finishing the last touches...',
  'Polishing to perfection',
  "Sorry for the wait, it'll be worth it!",
]

interface WsResponse extends WsResponseBase {
  content?: ContentType
  elementId?: number
  errorMessage?: string
}

export const getGenerateWsTopic = (contentId: string, elementId: number) =>
  `generate/${contentId}/${elementId}`

const ContentPage = () => {
  useDocumentTitle('Create Content | Phrasee')
  const queryClient = useQueryClient()
  const dispatch = useAppDispatch()

  const [nglPending, setNglPending] = useState(false)
  const {
    lastJsonMessage,
    sendJsonMessage,
    readyState,
  }: {
    lastJsonMessage: WsResponse
    sendJsonMessage: SendJsonMessage
    readyState: ReadyState
  } = useWebsockets()

  const { content } = useContent()

  useEffect(() => {
    if (content && lastJsonMessage) {
      const elementId = Number(lastJsonMessage.elementId)
      const wsTopic = getGenerateWsTopic(content._id, elementId)

      if (lastJsonMessage.topics?.includes(wsTopic)) {
        if (lastJsonMessage.status === 'success') {
          queryClient.setQueryData(
            contentKeys.content(content._id),
            lastJsonMessage.content
          )

          sendJsonMessage({
            action: 'unsubscribe',
            topics: [wsTopic],
          })

          dispatch(toggleElementsDisabled(false))
          dispatch(workflowCardClicked({ elementId }))
        }
      } else if (lastJsonMessage.status === 'error' && content) {
        queryClient.invalidateQueries(contentKeys.content(content._id))
        errorToast(lastJsonMessage.errorMessage)
        dispatch(toggleElementsDisabled(false))
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastJsonMessage])

  useEffect(() => {
    setNglPending(false)

    if (content && readyState === ReadyState.OPEN) {
      const allComponentElements = getAllComponentElements(content)
      allComponentElements.forEach((element) => {
        if (element.nlg_status === 'pending') {
          const wsTopic = getGenerateWsTopic(content._id, element.element_id)
          setNglPending(true)
          sendJsonMessage({
            action: 'subscribe',
            topics: [wsTopic],
          })
        }
      })
    }
  }, [content, readyState, sendJsonMessage])

  useEffect(() => {
    return () => {
      dispatch(contentPageCleanup())
    }
  }, [dispatch])

  return (
    <>
      <LoadingOverlay isLoading={nglPending} phrases={phrases} />
      <Header hasMaxWidth />
      <MainContent className="flex-wrap">
        <Workflow />
        <Content />
        <Preview />
      </MainContent>
    </>
  )
}

export default ContentPage
